import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { createPairingCode, saveProfile } from '../api/settings'
import type { MeResponse } from '../types'
import { Linked } from '../components/Linked'

/** The longest edge of a stored picture, in pixels. */
const AVATAR_PX = 128

/**
 * Shrink a chosen image to something worth storing.
 *
 * A phone photo is several megabytes; an avatar shown at 40 pixels needs none
 * of that. Resizing in the browser means the large file never leaves the
 * machine, and what is stored is a data URL of a few kilobytes.
 */
function toAvatar(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read that file'))
    reader.onload = () => {
      const img = new Image()
      img.onerror = () => reject(new Error('That file is not an image'))
      img.onload = () => {
        const side = Math.min(img.width, img.height)
        const canvas = document.createElement('canvas')
        canvas.width = AVATAR_PX
        canvas.height = AVATAR_PX

        const ctx = canvas.getContext('2d')
        if (!ctx) return reject(new Error('This browser cannot resize the picture'))

        // Square crop from the middle, so a portrait is not squashed.
        ctx.drawImage(
          img,
          (img.width - side) / 2,
          (img.height - side) / 2,
          side,
          side,
          0,
          0,
          AVATAR_PX,
          AVATAR_PX,
        )
        resolve(canvas.toDataURL('image/jpeg', 0.82))
      }
      img.src = String(reader.result)
    }
    reader.readAsDataURL(file)
  })
}

export function ProfileSection({
  me,
  compact,
  onChanged,
}: {
  me: MeResponse | null
  compact?: boolean
  onChanged?: () => void
}) {
  const { signOut, resetPassword } = useAuth()
  const [name, setName] = useState('')
  const [photo, setPhoto] = useState<string | null | undefined>(undefined)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState('')
  const [done, setDone] = useState('')
  const [error, setError] = useState('')

  // Seeded once per account, not on every change to `me`.
  //
  // useAppState refetches `me` on every window focus, and this used to copy
  // the stored name back over the field on each one — so alt-tabbing while
  // typing a new name silently discarded it.
  const [seeded, setSeeded] = useState('')

  useEffect(() => {
    if (!me?.uid || seeded === me.uid) return
    setName(me.displayName ?? '')
    setSeeded(me.uid)
  }, [me?.uid, me?.displayName, seeded])

  const shown = photo === undefined ? (me?.photoURL ?? null) : photo
  const dirty = name.trim() !== (me?.displayName ?? '') || photo !== undefined

  async function run(what: string, fn: () => Promise<string>) {
    setBusy(what)
    setError('')
    setDone('')
    try {
      setDone(await fn())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy('')
    }
  }

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    setError('')
    setDone('')
    try {
      setPhoto(await toAvatar(file))
    } catch {
      // HEIC is the common case: an iPhone photo no browser can decode.
      setError(`${file.name} could not be read. Try a JPEG or PNG.`)
    }
  }

  return (
    <div className="card">
      <h2>Profile</h2>
      <p className="hint">Your name and picture. Customers never see them.</p>

      <div className="avatar-row">
        {shown ? (
          <img className="avatar" src={shown} alt="" />
        ) : (
          <span className="avatar avatar-blank" aria-hidden="true">
            {(me?.email ?? '?').slice(0, 1).toUpperCase()}
          </span>
        )}

        <div className="avatar-actions">
          <label className="linkish file-label">
            {shown ? 'Change picture' : 'Add a picture'}
            <input type="file" accept="image/*" onChange={pick} />
          </label>

          {shown && (
            <button className="linkish" onClick={() => setPhoto(null)}>
              Remove
            </button>
          )}
        </div>
      </div>

      <label className="field">
        <span>Name</span>
        <input
          type="text"
          value={name}
          placeholder="Shown on nothing a customer sees"
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
        />
      </label>

      <label className="field">
        <span>Email</span>
        <input type="email" value={me?.email ?? ''} disabled readOnly />
      </label>

      <button
        disabled={Boolean(busy) || !dirty}
        onClick={() =>
          void run('save', async () => {
            await saveProfile(name.trim(), photo)
            setPhoto(undefined)
            onChanged?.()
            return 'Saved.'
          })
        }
      >
        {busy === 'save' ? 'Saving…' : 'Save profile'}
      </button>

      <hr />

      <button
        className="secondary"
        disabled={Boolean(busy) || !me?.email}
        onClick={() =>
          void run('reset', async () => {
            await resetPassword(me!.email!)
            return `Sent a reset link to ${me!.email}.`
          })
        }
      >
        {busy === 'reset' ? 'Sending…' : 'Change password'}
      </button>

      {/* Only useful on the standalone page — this is where a Google user
          generates the code they paste into the widget. */}
      {!compact && (
        <button
          className="secondary"
          disabled={Boolean(busy)}
          onClick={() => void run('pair', async () => {
            setCode((await createPairingCode()).code)
            return ''
          })}
        >
          {busy === 'pair' ? 'Making a code…' : 'Link my widget'}
        </button>
      )}

      {code && (
        <p className="note">
          Paste this into the widget within 10 minutes:{' '}
          <strong style={{ letterSpacing: '0.2em', fontSize: 15 }}>{code}</strong>
        </p>
      )}

      <button className="secondary" onClick={() => void signOut()}>
        Sign out
      </button>

      {done && <p className="note done">{done}</p>}
      {error && <p className="inline-error"><Linked text={error} /></p>}
    </div>
  )
}
